import { flatten, get } from "lodash";
import { useMemo } from "react";
import { useQuery } from "shared/hooks/useApi";
import {
  ATTACHMENTS,
  INSURANCE_PLANS_DOCUMENT_STEPS,
} from "utils/api/graphql/queries/attachments";
import {
  DOCUMENT_STEPS,
  DOCUMENTS_TYPES,
  INSURANCE_PLAN_ATTACHMENTS_TYPES,
} from "utils/constants";
import { filterAttachmentsByStep } from "utils/helpers/documentStepsConfig";
import useStaticAttachments from "shared/hooks/useStaticAttachments";

const identityUpsells = (upsells) => upsells;

export const useSubscriptionDocumentAttachments = (
  subscription,
  {
    extraTypes = [DOCUMENTS_TYPES.QUOTE],
    step = DOCUMENT_STEPS.CONTRAT,
    filterUpsells = identityUpsells,
  } = {},
) => {
  const basePlanId = get(subscription, "insurancePlan.id", "");
  const subscriptionId = get(subscription, "id");
  const projectId = get(subscription, "project.id");

  const rawUpsells = get(
    subscription,
    `project.fields.upsells.${basePlanId}`,
    [],
  );
  const upsells = filterUpsells(Array.isArray(rawUpsells) ? rawUpsells : []);
  const upsellPlanIds = (Array.isArray(upsells) ? upsells : [])
    .map((upsell) => get(upsell, "insurancePlan.id", ""))
    .filter(Boolean);
  const upsellPlanIdsKey = upsellPlanIds.join("|");
  const extraTypesKey = extraTypes.join("|");
  const shouldLoadUpsellSteps = Boolean(step) && upsellPlanIds.length > 0;

  const variables = useMemo(
    () => ({
      where: {
        OR: [
          {
            subscription: {
              id: subscriptionId,
            },
          },
          {
            AND: [
              {
                project: {
                  id: projectId,
                },
              },
              {
                insurancePlans: {
                  id: basePlanId,
                },
              },
            ],
          },
        ],
      },
      isIn: {
        type: [
          ...Object.values(INSURANCE_PLAN_ATTACHMENTS_TYPES),
          ...extraTypes,
        ],
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed via join strings
    [basePlanId, subscriptionId, projectId, extraTypesKey],
  );

  const staticTypes = useMemo(
    () => [...Object.values(INSURANCE_PLAN_ATTACHMENTS_TYPES), ...extraTypes],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed via join strings
    [extraTypesKey],
  );
  const staticUpsellPlanIds = useMemo(
    () => upsellPlanIds,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed via join strings
    [upsellPlanIdsKey],
  );
  const { attachments: staticAttachments, loading: staticLoading } =
    useStaticAttachments({
      projectId,
      insurancePlanIds: basePlanId ? [basePlanId] : [],
      upsellPlanIds: staticUpsellPlanIds,
      step,
      types: staticTypes,
    });

  const { data, loading: attachmentsLoading } = useQuery(ATTACHMENTS, {
    variables,
  });

  const { data: upsellPlansData, loading: upsellPlansLoading } = useQuery(
    INSURANCE_PLANS_DOCUMENT_STEPS,
    {
      skip: !shouldLoadUpsellSteps,
      variables: {
        isIn: { id: upsellPlanIds },
        take: upsellPlanIds.length,
      },
    },
  );

  const attachments = useMemo(() => {
    const rawAttachments = get(data, "attachments.data", []);
    if (!step) return [...rawAttachments, ...staticAttachments];

    // Nested attachment.insurancePlans is often null for brokers, so load
    // upsell DOCUMENT_STEPS from the upsell plans themselves.
    const upsellParams = flatten(
      get(upsellPlansData, "insurancePlans.data", []).map(
        (plan) => get(plan, "params") || [],
      ),
    );

    return [
      ...filterAttachmentsByStep({
        attachments: rawAttachments,
        params: [
          ...get(subscription, "insurancePlan.params", []),
          ...upsellParams,
        ],
        step,
      }),
      ...staticAttachments,
    ];
  }, [data, step, subscription, upsellPlansData, staticAttachments]);

  const loading =
    attachmentsLoading ||
    staticLoading ||
    (shouldLoadUpsellSteps && upsellPlansLoading);

  return { attachments, loading };
};

export default useSubscriptionDocumentAttachments;
